import {
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Type,
  inject,
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { LeafButton, LeafLink } from '@cigna/leaf-web/lib/angular';
import { TrackUtil } from '@cigna/shared/angular/analytics-ui';

type TrackedClick = MouseEvent & { __cignaTrackClicks?: boolean };

interface LeafClickSource<T> {
  component: Type<T>;
  clicks: (instance: T) => Observable<Event>;
}

const leafClickSource = <T>(
  component: Type<T>,
  clicks: (instance: T) => Observable<Event>,
): LeafClickSource<T> => ({ component, clicks });

// To support another Leaf component, add it here and add its tag to the directive selector below.
const LEAF_CLICK_SOURCES = [
  leafClickSource(LeafButton, (button) => button.onClick),
  leafClickSource(LeafLink, (link) => link.onNavigate),
];

// Leaf's inner clicks don't reliably reach the ng-leaf-* host, so track from the component's click output instead.
@Directive({
  selector: 'ng-leaf-button[cignaTrackClicks], ng-leaf-link[cignaTrackClicks]',
})
export class LeafTrackClicksDirective implements OnInit, OnDestroy {
  @Input({ alias: 'cignaTrackClicks', required: true }) params!:
    | string
    | [string, ...unknown[]];

  private clicks$ = this.resolveClicks();
  private host: HTMLElement = inject(ElementRef).nativeElement;
  private trackUtil = inject(TrackUtil);
  private subscription?: Subscription;

  // Runs in capture, before Leaf emits, so TrackClicksDirective skips the native click wherever it lands.
  private markHandled = (event: TrackedClick) => {
    event.__cignaTrackClicks = true;
  };

  ngOnInit(): void {
    this.host.addEventListener('click', this.markHandled, true);
    this.subscription = this.clicks$.subscribe((event) => {
      const [eventName, ...extras] =
        typeof this.params === 'string' ? [this.params] : this.params;
      this.trackUtil.trackMouseEvent(eventName, event as MouseEvent, ...extras);
    });
  }

  ngOnDestroy(): void {
    this.host.removeEventListener('click', this.markHandled, true);
    this.subscription?.unsubscribe();
  }

  private resolveClicks(): Observable<Event> {
    for (const source of LEAF_CLICK_SOURCES as Array<
      LeafClickSource<unknown>
    >) {
      const instance = inject(source.component, {
        self: true,
        optional: true,
      });
      if (instance) {
        return source.clicks(instance);
      }
    }
    throw new Error(
      'LeafTrackClicksDirective: host is not a registered Leaf component',
    );
  }
}
